import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Swords, SlidersHorizontal, X, ArrowUp, ArrowDown, Dices } from "lucide-react";

export default function CombatBanner({ game, combat, isGm }) {
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideLabel, setOverrideLabel] = useState("");
  const [overrideTotal, setOverrideTotal] = useState("");

  if (!combat?.active || !combat.order?.length) return null;
  const current = combat.order[combat.turn_index];

  const nextTurn = async () => {
    await api.post(`/games/${game.id}/combat/next`);
  };
  const endCombat = async () => {
    await api.post(`/games/${game.id}/combat/end`);
  };
  const jumpToTurn = async (idx) => {
    await api.patch(`/games/${game.id}/combat`, { turn_index: idx });
  };
  const moveCombatant = async (idx, dir) => {
    const newOrder = [...combat.order];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    await api.patch(`/games/${game.id}/combat`, { order: newOrder });
  };
  const removeCombatant = async (idx) => {
    const newOrder = combat.order.filter((_, i) => i !== idx);
    await api.patch(`/games/${game.id}/combat`, { order: newOrder });
  };
  const postOverride = async () => {
    if (!overrideLabel.trim() || overrideTotal === "") return;
    try {
      await api.post(`/games/${game.id}/chat/gm-override`, { label: overrideLabel, total: parseInt(overrideTotal, 10) || 0 });
      toast.success("Override posted to chat");
      setOverrideLabel("");
      setOverrideTotal("");
    } catch {
      toast.error("Could not post override");
    }
  };

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 px-5 py-2.5 rounded-full bg-black/70 backdrop-blur-2xl border border-[#D90429]/40" data-testid="combat-banner">
      <Swords className="w-4 h-4 text-[#D90429]" />
      <span className="text-xs uppercase tracking-widest text-[#f1e0c5]/60">Round {combat.round}</span>
      <span className="font-display text-lg text-[#D4AF37]" data-testid="combat-current-turn">{current?.name}</span>
      {isGm && (
        <>
          <Button data-testid="combat-next-turn-button" size="sm" onClick={nextTurn} className="rounded-full h-7 px-3 bg-[#D90429] hover:bg-[#b8031f] text-xs">
            Next Turn
          </Button>
          <Popover open={overrideOpen} onOpenChange={setOverrideOpen}>
            <PopoverTrigger asChild>
              <button data-testid="combat-override-button" className="w-7 h-7 rounded-full flex items-center justify-center text-[#f1e0c5]/50 hover:text-[#D4AF37]" title="GM Override">
                <SlidersHorizontal className="w-3.5 h-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="bg-[#322216] border-white/10 text-[#f1e0c5] w-72 p-3 space-y-3" align="center" data-testid="combat-override-panel">
              <div>
                <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Turn Order — click to jump</p>
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {combat.order.map((c, idx) => (
                    <div key={c.token_id || idx} className={`flex items-center gap-1.5 rounded-lg px-2 py-1 ${idx === combat.turn_index ? "bg-[#D90429]/20" : "bg-black/20"}`}>
                      <button data-testid={`override-jump-turn-${idx}`} onClick={() => jumpToTurn(idx)} className="flex-1 text-left text-xs truncate">
                        {c.name} <span className="text-[#f1e0c5]/30">({c.initiative})</span>
                      </button>
                      <button data-testid={`override-move-up-${idx}`} onClick={() => moveCombatant(idx, -1)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37]"><ArrowUp className="w-3 h-3" /></button>
                      <button data-testid={`override-move-down-${idx}`} onClick={() => moveCombatant(idx, 1)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37]"><ArrowDown className="w-3 h-3" /></button>
                      <button data-testid={`override-remove-${idx}`} onClick={() => removeCombatant(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t border-white/10 pt-3">
                <p className="text-[10px] uppercase text-[#f1e0c5]/40 mb-1.5">Post Manual Roll</p>
                <div className="flex gap-1.5">
                  <Input data-testid="override-roll-label-input" value={overrideLabel} onChange={(e) => setOverrideLabel(e.target.value)} placeholder="Label" className="bg-black/30 border-white/10 h-8 text-xs flex-1" />
                  <Input data-testid="override-roll-total-input" type="number" value={overrideTotal} onChange={(e) => setOverrideTotal(e.target.value)} placeholder="Total" className="bg-black/30 border-white/10 h-8 text-xs w-16" />
                  <Button data-testid="override-roll-post-button" size="sm" onClick={postOverride} className="h-8 rounded-full bg-[#D90429] hover:bg-[#b8031f] px-2.5">
                    <Dices className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <button data-testid="combat-end-button" onClick={endCombat} className="text-[#f1e0c5]/40 hover:text-[#D90429] text-xs uppercase tracking-wider">
            End
          </button>
        </>
      )}
    </div>
  );
}
